/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Component } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid,
	TextField,
	Theme,
	Typography
} from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ModeCommentIcon from '@mui/icons-material/ModeComment';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { grapevineAdminuiStatisticsApi } from '../../../ServiceFactory';
import { ClientGeoAreaExtended, ClientGrapevineGeoAreaInfo } from '@DigitaleDoerfer/digitale-doerfer-api';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import BarLoadingIndicator from '../../../shared/views/LoadingIndicators/BarLoadingIndicator.view';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const styles = (theme: Theme) => {
	return createStyles({
		geoAreaCell: {
			width: '40%',
			marginBottom: '2px'
		},
		geoAreaName: {
			marginTop: '2px',
			cursor: 'default'
		},
		tenantNameCell: {
			fontWeight: 600
		},
		copyIcon: {
			marginTop: '4px',
			height: '16px',
			marginLeft: '5px',
			cursor: 'pointer',
			color: theme.palette.primary.main
		},
		copyIconParent: {
			height: '25px',
			minWidth: '10px'
		},
		statisticIcon: {
			cursor: 'pointer',
			color: theme.palette.primary.main
		},
		statisticContainer: {
			marginLeft: '10px'
		},
		idContainer: {
			display: 'flex',
			alignItems: 'center',
			height: '100%'
		}
	});
};

interface Props extends WithStyles<typeof styles> {
	openStatisticDialog: boolean;
	wasStatsCopied: boolean;
	geoArea: ClientGeoAreaExtended;
	handleCopyStatsButton: (geoAreaStatistics?: ClientGrapevineGeoAreaInfo) => void;
	handleCloseStatisticsButton: () => void;
}
interface State {
	isLoading: boolean;
	geoAreaStatistics?: ClientGrapevineGeoAreaInfo;
}

class StatisticsView extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			geoAreaStatistics: undefined,
			isLoading: true
		};
	}

	componentDidMount(): void {
		const { geoArea } = this.props;
		this.getGrapevineStatsForGeoArea(geoArea);
	}

	async getGrapevineStatsForGeoArea(geoArea: ClientGeoAreaExtended): Promise<void> {
		const geoAreaStatisticResponse: ClientGrapevineGeoAreaInfo =
			await grapevineAdminuiStatisticsApi().getGrapevineStatsForGeoArea({
				geoAreaId: geoArea?.id
			});
		this.setState({ geoAreaStatistics: geoAreaStatisticResponse, isLoading: false });
	}

	render(): JSX.Element {
		const {
			openStatisticDialog,
			geoArea,
			wasStatsCopied,
			classes,
			handleCopyStatsButton,
			handleCloseStatisticsButton
		} = this.props;
		const { geoAreaStatistics, isLoading } = this.state;

		return (
			<Dialog
				open={openStatisticDialog}
				onClose={handleCloseStatisticsButton}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">
					<Grid container>
						<Grid
							item
							xs
							onClick={(): void => handleCopyStatsButton(geoAreaStatistics)}
							className={classes.copyIconParent}
						>
							{!wasStatsCopied && (
								<Box>
									<Typography component="span" variant="h2">
										Zahlen für {geoArea.name}
									</Typography>
									<FileCopyOutlinedIcon className={classes.copyIcon} />
								</Box>
							)}
							{wasStatsCopied && (
								<Box>
									<Typography component="span" variant="h2">
										Zahlen für {geoArea.name}
									</Typography>
									<CheckCircleIcon className={classes.copyIcon} />
								</Box>
							)}
						</Grid>
					</Grid>
				</DialogTitle>
				<DialogContent>
					{isLoading ? (
						<BarLoadingIndicator loading={isLoading} />
					) : (
						<>
							<DialogContentText id="alert-dialog-description">
								<Grid container spacing={2}>
									<Grid item container direction="row" alignItems="center" xs={4}>
										<Grid item xs={2}>
											<PeopleAltIcon />
										</Grid>
										<Grid item xs={6} className={classes.statisticContainer}>
											<Typography component="span" variant="subtitle2">
												Registrierte Nutzer
											</Typography>
										</Grid>
										<Grid item xs={12}>
											<TextField
												variant="standard"
												defaultValue="0"
												value={geoAreaStatistics?.personCount ?? '0'}
												size="small"
												style={{ width: '50%' }}
												InputProps={{
													readOnly: true
												}}
											/>
										</Grid>
									</Grid>
									<Grid item container direction="row" alignItems="center" xs={4}>
										<Grid item xs={2}>
											<PostAddIcon />
										</Grid>
										<Grid item xs={6} className={classes.statisticContainer}>
											<Typography component="span" variant="subtitle2">
												Anzahl Posts
											</Typography>
										</Grid>
										<Grid item xs={12}>
											<TextField
												variant="standard"
												defaultValue="0"
												value={geoAreaStatistics?.postCount ?? '0'}
												size="small"
												style={{ width: '50%' }}
												InputProps={{
													readOnly: true
												}}
											/>
										</Grid>
									</Grid>
									<Grid item container direction="row" alignItems="center" xs={4}>
										<Grid item>
											<ModeCommentIcon />
										</Grid>
										<Grid item xs={6} className={classes.statisticContainer}>
											<Typography component="span" variant="subtitle2">
												Anzahl Kommentare
											</Typography>
										</Grid>
										<Grid item xs={12}>
											<TextField
												variant="standard"
												defaultValue="0"
												value={geoAreaStatistics?.commentCount ?? '0'}
												size="small"
												style={{ width: '50%' }}
												InputProps={{
													readOnly: true
												}}
											/>
										</Grid>
									</Grid>
								</Grid>
							</DialogContentText>
						</>
					)}
				</DialogContent>

				<DialogActions>
					<Button variant="contained" onClick={handleCloseStatisticsButton}>
						VERBERGEN
					</Button>
				</DialogActions>
			</Dialog>
		);
	}
}
export default withStyles(styles)(StatisticsView);
